import React, { useEffect, useState } from "react";
import "./Search.css";
import { FaSearch } from "react-icons/fa";
import HomeHeader from "../../components/header/HomeHeader";
import CommonFooter from "../../components/footer/CommonFooter";
import { appApiHandler } from "../../api/appApiHandler";
import FullScreenLoader from "../../components/loader/fullScreenLoader/FullscreenLoader";
import Breadcrumb from "../../components/breadcrumb";
import EmptyState from "../../components/EmptyState";
import GridProductCard from "../../components/gridProductCard";

const SearchPage = () => {
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [gridColumns, setGridColumns] = useState(4);
  const [effectiveGridColumns, setEffectiveGridColumns] = useState(4);

  const getSearchProducts = async (search = "") => {
    setLoading(true);
    const reqData = { search };
    const response = await appApiHandler.searchProducts(reqData);
    setLoading(false);
    if (response?.status === 200) {
      setProducts(response?.data || []);
    }
  };

  useEffect(() => {
    getSearchProducts();
  }, []);

  useEffect(() => {
    if (!searchText.trim()) {
      getSearchProducts();
    }
  }, [searchText]);

  const onPressSearch = () => {
    getSearchProducts(searchText);
  };

  const getEffectiveGridColumns = () => {
    if (typeof window !== "undefined" && window.innerWidth <= 768) {
      // On small devices, limit to max 3 columns and default to 2
      // If user selects 4, 5, or 6 columns, default to 2 columns
      if (gridColumns >= 4) {
        return 2;
      }
      return Math.min(gridColumns, 3);
    }
    return gridColumns;
  };

  // Auto search on typing with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchText.trim()) {
        getSearchProducts(searchText);
      } else {
        getSearchProducts();
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [searchText]);

  // Update effective grid columns when gridColumns or window size changes
  useEffect(() => {
    const updateEffectiveColumns = () => {
      if (typeof window !== "undefined" && window.innerWidth <= 768) {
        // On small devices, limit to max 3 columns and default to 2
        // If user selects 4, 5, or 6 columns, default to 2 columns
        if (gridColumns >= 4) {
          setEffectiveGridColumns(2);
        } else {
          setEffectiveGridColumns(Math.min(gridColumns, 3));
        }
      } else {
        setEffectiveGridColumns(gridColumns);
      }
    };

    updateEffectiveColumns();

    const handleResize = () => {
      updateEffectiveColumns();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [gridColumns]);

  return (
    <div className="searchPage">
      <HomeHeader />
      <div className="page-content">
        <Breadcrumb
          items={[
            {
              label: "Home",
              path: "/",
            },
            {
              label: "Search",
              isActive: true,
            },
          ]}
        />
        <main className="searchPage__main">
          <div className="searchPage__form">
            <div className="searchPage__inputWrap">
              <FaSearch size={16} className="searchPage__inputIcon" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="searchPage__input with-icon"
              />
            </div>

            {
              products?.length > 0 && (
                <div className="searchPage__gridSelector">
                  <div className="searchPage__gridOptions">
                    {[1, 2, 3, 4, 5, 6].map((cols) => (
                      <button
                        key={cols}
                        className={`searchPage__gridOption ${effectiveGridColumns === cols ? 'active' : ''}`}
                        onClick={() => setGridColumns(cols)}
                        title={`${cols} column${cols > 1 ? 's' : ''}`}
                      >
                        <div className={`grid-icon grid-icon--${cols}`}>
                          {Array.from({ length: cols }).map((_, i) => (
                            <div key={i} className="grid-icon-item" />
                          ))}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )
            }
          </div>

          <section className="searchPage__results">
            {products?.length > 0 ? (
              <div className={`searchPage__list searchPage__list--${effectiveGridColumns}`}>
                {products?.map((item) => (
                  <GridProductCard key={item?._id} item={item} gridColumns={gridColumns} />
                ))}
              </div>
            ) : (
              <EmptyState
                icon={FaSearch}
                title="No products found matching your search."
                iconSize={40}
              />
            )}
          </section>
        </main>
      </div>
      <CommonFooter />
      {loading && <FullScreenLoader text="Searching..." />}
    </div>
  );
};

export default SearchPage;
